import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react'
import {
  UnifiedGlobalState,
  UnifiedAction,
  ProjectInfo,
  AHUData,
  AHUFormData,
  FanSelectionData,
  LegacyProjectData,
  LegacyAHUData,
  STORAGE_KEYS
} from '../types/unified-data'

// =============================================
// 초기 상태
// =============================================

const initialState: UnifiedGlobalState = {
  version: 2.7,
  appOption: 'A',
  
  projects: [],
  currentProjectId: null,
  
  ahus: [],
  currentAHUId: null,
  
  ui: {
    activeForm: null,
    activeTab: 0,
    sidebarCollapsed: false
  },
  
  tempData: {
    ahuCalculationResults: null,
    fanCalculationResults: null
  },
  
  isLoaded: false,
  
  settings: {
    autoSave: true,
    autoSaveInterval: 30000, // 30초
    showCalculationDetails: false,
    defaultUnits: {
      temperature: 'C',
      pressure: 'Pa', 
      airflow: 'CMH'
    }
  }
}

// =============================================
// 레거시 데이터 마이그레이션 함수들
// =============================================

/** 기존 ProjectData를 새로운 ProjectInfo로 변환 */
const migrateProjectData = (legacy: LegacyProjectData): ProjectInfo => {
  // 기존 개별 외기조건 필드들을 customOutdoorConditions로 변환
  const customOutdoorConditions: { [code: string]: { summer: any, winter: any } } = {};
  
  // 기존 데이터가 있으면 현재 외기조건 코드에 대해 커스텀 조건으로 저장
  if (legacy.summerOutdoorTemp && legacy.summerOutdoorHumidity && 
      legacy.winterOutdoorTemp && legacy.winterOutdoorHumidity) {
    customOutdoorConditions[legacy.outdoorConditionCode] = {
      summer: {
        temperature: legacy.summerOutdoorTemp,
        humidity: legacy.summerOutdoorHumidity,
        dailyRange: legacy.summerDailyRange || '10' // 기본값
      },
      winter: {
        temperature: legacy.winterOutdoorTemp,
        humidity: legacy.winterOutdoorHumidity
      }
    };
  }

  return {
    id: legacy.id,
    projectName: legacy.projectName,
    buildingName: legacy.buildingName,
    location: legacy.location,
    buildingType: legacy.buildingType,
    totalFloors: legacy.totalFloors,
    basementFloors: legacy.basementFloors,
    buildingArea: legacy.buildingArea,
    outdoorConditionCode: legacy.outdoorConditionCode,
    altitude: legacy.altitude || '0', // 기본값
    customOutdoorConditions: Object.keys(customOutdoorConditions).length > 0 ? customOutdoorConditions : undefined,
    summerIndoorTemp: legacy.summerIndoorTemp,
    summerIndoorHumidity: legacy.summerIndoorHumidity,
    winterIndoorTemp: legacy.winterIndoorTemp,
    winterIndoorHumidity: legacy.winterIndoorHumidity,
    createdAt: legacy.createdAt,
    updatedAt: legacy.updatedAt
  }
}

/**
 * 이전 필드명(sensibleHeatLoad/latentHeatLoad) → 신규 필드명(sensibleCoolingLoad/latentCoolingLoad) 호환 정규화.
 * 구 `.dcs` 파일이나 리네임 이전 localStorage 데이터를 읽을 때 사용.
 * - 신규 필드가 있으면 유지, 없으면 구 필드 값을 복사
 * - 구 필드명은 제거하여 react-hook-form에 재등록되지 않도록 한다
 */
export const normalizeAHUFormCoolingFields = (formData: any): any => {
  if (!formData || typeof formData !== 'object') return formData
  const hasOldSensible = Object.prototype.hasOwnProperty.call(formData, 'sensibleHeatLoad')
  const hasOldLatent = Object.prototype.hasOwnProperty.call(formData, 'latentHeatLoad')
  const hasNewSensible = Object.prototype.hasOwnProperty.call(formData, 'sensibleCoolingLoad')
  const hasNewLatent = Object.prototype.hasOwnProperty.call(formData, 'latentCoolingLoad')
  if (!hasOldSensible && !hasOldLatent && hasNewSensible && hasNewLatent) {
    return formData // 이미 순수 신규 포맷
  }
  const { sensibleHeatLoad, latentHeatLoad, ...rest } = formData
  return {
    ...rest,
    sensibleCoolingLoad: hasNewSensible ? formData.sensibleCoolingLoad : (sensibleHeatLoad ?? '0'),
    latentCoolingLoad: hasNewLatent ? formData.latentCoolingLoad : (latentHeatLoad ?? '0'),
  }
}

/** 기존 AHUData를 새로운 구조로 변환 */
const migrateAHUData = (legacy: LegacyAHUData): AHUData => {
  const normalizedLegacyFormData: any = normalizeAHUFormCoolingFields(legacy.formData) || {}
  // formData를 타입 안전하게 변환
  const formData: AHUFormData = {
    // 기본 정보
    ahuNo: normalizedLegacyFormData.ahuNo || legacy.ahuNo,
    systemName: normalizedLegacyFormData.systemName || legacy.systemName,
    quantity: normalizedLegacyFormData.quantity || legacy.quantity,
    location: normalizedLegacyFormData.location || legacy.location,

    // 공간 정보
    conditionedArea: normalizedLegacyFormData.conditionedArea || '',
    ceilingHeight: normalizedLegacyFormData.ceilingHeight || '',
    occupantCount: normalizedLegacyFormData.occupantCount || '',

    // 공조 부하
    sensibleCoolingLoad: normalizedLegacyFormData.sensibleCoolingLoad || '',
    latentCoolingLoad: normalizedLegacyFormData.latentCoolingLoad || '',
    ventilationLightingLoad: normalizedLegacyFormData.ventilationLightingLoad || '',
    heatingLoad: normalizedLegacyFormData.heatingLoad || '',
    raLightingLoadMode: normalizedLegacyFormData.raLightingLoadMode || 'included',

    // 장비 기준
    outdoorDesignCondition: normalizedLegacyFormData.outdoorDesignCondition || 'A',
    outdoorApplicationTime: normalizedLegacyFormData.outdoorApplicationTime || '15',
    systemType: normalizedLegacyFormData.systemType || 'CAV',
    fanType: normalizedLegacyFormData.fanType || 'None',

    // 코일 설정
    coolingCoilType: normalizedLegacyFormData.coolingCoilType || 'Water',
    reheatingCoilType: normalizedLegacyFormData.reheatingCoilType || 'Water',
    preheatingCoilType: normalizedLegacyFormData.preheatingCoilType || 'None',
    heatingCoilType: normalizedLegacyFormData.heatingCoilType || 'None',
    humidifierType: normalizedLegacyFormData.humidifierType || 'None',

    // 필터 설정
    preFilterType: normalizedLegacyFormData.preFilterType || 'None',
    mediumFilterType: normalizedLegacyFormData.mediumFilterType || 'None',

    // 배열 회수기
    heatRecoveryType: normalizedLegacyFormData.heatRecoveryType || 'None',
    heatRecoveryCoolingEfficiency: normalizedLegacyFormData.heatRecoveryCoolingEfficiency || '',
    heatRecoveryHeatingEfficiency: normalizedLegacyFormData.heatRecoveryHeatingEfficiency || '',

    // 급기/환기 비율
    saAirflowRatio: normalizedLegacyFormData.saAirflowRatio || '100',
    oaAirflowRatio: normalizedLegacyFormData.oaAirflowRatio || '20',
    eaAirflowRatio: normalizedLegacyFormData.eaAirflowRatio || '20',
    raAirflowRatio: normalizedLegacyFormData.raAirflowRatio || '80',

    // 팬 설정
    sfLoadPercentage: normalizedLegacyFormData.sfLoadPercentage,
    rfLoadPercentage: normalizedLegacyFormData.rfLoadPercentage,

    // 추가 옵션
    silencerType: normalizedLegacyFormData.silencerType || 'None',
    damperType: normalizedLegacyFormData.damperType || 'None',
    insulation: normalizedLegacyFormData.insulation || false,
    weatherCover: normalizedLegacyFormData.weatherCover || false,
    drainPan: normalizedLegacyFormData.drainPan || false,
    accessDoor: normalizedLegacyFormData.accessDoor || false,

    // 메모
    notes: normalizedLegacyFormData.notes || ''
  }

  return {
    id: legacy.id,
    projectId: legacy.projectId,
    ahuNo: legacy.ahuNo,
    systemName: legacy.systemName,
    quantity: legacy.quantity,
    location: legacy.location,
    formData,
    createdAt: legacy.createdAt,
    updatedAt: legacy.updatedAt
  }
}

// =============================================
// 로컬스토리지 유틸리티
// =============================================

/** 새로운 통합 데이터 구조로 저장 */
const saveToStorage = (state: UnifiedGlobalState): void => {
  try {
    // 이미지 및 대용량 데이터를 제외한 state 생성 (localStorage 용량 초과 방지)
    const stateWithoutImages = {
      ...state,
      ahus: state.ahus.map(ahu => {
        const { psychroChartImage, psychroChartData, ...ahuWithoutImage } = ahu;
        return ahuWithoutImage;
      })
    };

    localStorage.setItem(STORAGE_KEYS.MAIN_DATA, JSON.stringify(stateWithoutImages))
  } catch (error) {
    console.error('Failed to save unified state to localStorage:', error)
    // 용량 초과 시 사용자에게 알림
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('⚠️ LocalStorage 용량 초과 - 이미지 데이터는 세션에만 유지됩니다.')
    }
  }
}

/** 새로운 구조 또는 레거시 구조에서 데이터 로드 */
const loadFromStorage = (): Partial<UnifiedGlobalState> | null => {
  try {
    // 1. 새로운 통합 구조에서 로드 시도
    const unifiedData = localStorage.getItem(STORAGE_KEYS.MAIN_DATA)
    if (unifiedData) {
      const parsed = JSON.parse(unifiedData)
      // 이전 필드명(sensibleHeatLoad/latentHeatLoad)으로 저장된 통합 데이터 호환 처리
      if (Array.isArray(parsed?.ahus)) {
        parsed.ahus = parsed.ahus.map((ahu: any) =>
          ahu && ahu.formData
            ? { ...ahu, formData: normalizeAHUFormCoolingFields(ahu.formData) }
            : ahu
        )
      }
      return parsed
    }

    // 2. 레거시 구조에서 마이그레이션
    return migrateLegacyData()
  } catch (error) {
    console.error('Failed to load from localStorage:', error)
    return null
  }
}

/** 레거시 데이터 마이그레이션 */
const migrateLegacyData = (): Partial<UnifiedGlobalState> | null => {
  try {
    // 기존 GlobalState 로드 (같은 키를 사용하므로 직접 확인)
    const existingData = localStorage.getItem(STORAGE_KEYS.MAIN_DATA)
    if (!existingData) return null
    
    // 이미 새 구조인지 확인
    try {
      const parsed = JSON.parse(existingData)
      if (parsed.settings && parsed.ui) {
        // 새 구조로 판단
        return null
      }
    } catch (e) {
      // 파싱 실패하면 레거시 데이터로 처리
    }

    const legacy = JSON.parse(existingData)
    
    // 프로젝트 데이터 마이그레이션
    const projects: ProjectInfo[] = (legacy.projects || []).map(migrateProjectData)
    
    // AHU 데이터 마이그레이션
    const ahus: AHUData[] = (legacy.ahus || []).map(migrateAHUData)
    
    // Fan Selection 데이터 통합
    const fanSelectionData = loadLegacyFanData()
    if (fanSelectionData && legacy.currentAHUId) {
      const currentAHU = ahus.find(ahu => ahu.id === legacy.currentAHUId)
      if (currentAHU) {
        currentAHU.fanSelectionData = fanSelectionData
      }
    }

    // UI 상태 마이그레이션
    const legacyUI = localStorage.getItem(STORAGE_KEYS.LEGACY_UI_STATE)
    const ui = legacyUI ? JSON.parse(legacyUI) : {}

    const migratedState: Partial<UnifiedGlobalState> = {
      version: legacy.version || 2.7,
      appOption: legacy.option || 'A',
      projects,
      currentProjectId: legacy.currentProjectId,
      ahus,
      currentAHUId: legacy.currentAHUId,
      ui: {
        activeForm: legacy.activeForm || null,
        activeTab: ui.activeTab ? parseInt(ui.activeTab, 10) : 0,
        sidebarCollapsed: false
      },
      tempData: {
        ahuCalculationResults: legacy.ahuCalculationResults || null,
        fanCalculationResults: null
      },
      isLoaded: false,
      settings: initialState.settings
    }

    return migratedState
  } catch (error) {
    console.error('Failed to migrate legacy data:', error)
    return null
  }
}

/** 레거시 Fan Selection 데이터 로드 */
const loadLegacyFanData = (): FanSelectionData | null => {
  try {
    const fanData = localStorage.getItem(STORAGE_KEYS.LEGACY_FAN_SELECTION)
    if (!fanData) return null

    const legacy = JSON.parse(fanData)
    
    // 레거시 FanSelectionData를 새 구조로 변환
    // (여기서는 기본 구조만 생성, 실제 데이터는 필요에 따라 매핑)
    return {
      criteria: {
        saFanPosition: legacy.criteria?.saFanPosition || 'Draw(I)',
        saFanRatio: legacy.criteria?.saFanRatio || 100,
        raFanPosition: legacy.criteria?.raFanPosition || 'Inside',
        raFanRatio: legacy.criteria?.raFanRatio || 100,
        safetyMode: legacy.criteria?.safetyMode || 'Ext.',
        rfEquipment: legacy.criteria?.rfEquipment || '',
        safetyPercentage1: legacy.criteria?.safetyPercentage1 || 10,
        safetyPercentage2: legacy.criteria?.safetyPercentage2 || 10
      },
      supplyFan: convertLegacyFanData(legacy.supplyFan),
      returnFan: convertLegacyFanData(legacy.returnFan),
      sfStaticPressure: legacy.sfStaticPressure || { calculated: '', selected: '', total: '' },
      rfStaticPressure: legacy.rfStaticPressure || { calculated: '', selected: '', total: '' }
    }
  } catch (error) {
    console.error('Failed to load legacy fan data:', error)
    return null
  }
}

/** 레거시 팬 데이터 변환 */
const convertLegacyFanData = (legacyFan: any): any => {
  if (!legacyFan) {
    return {
      position: '',
      loadPercentage: '',
      equipmentNumber: '',
      airflow: '',
      staticPressure: '',
      motorPower: '',
      efficiency: '',
      rpm: '',
      safetyFactor: '',
      ductPressureLoss: {
        supplyDuctFriction: '',
        supplyDuctLength: '',
        returnDuctFriction: '',
        returnDuctLength: '',
        supplyAirDevicePressureLoss: '',
        returnAirDevicePressureLoss: '',
        preFilterPressureLoss: '',
        mediumFilterPressureLoss: '',
        heatingCoilPressureLoss: '',
        coolingCoilPressureLoss: '',
        humidifierPressureLoss: '',
        soundAttenuatorPressureLoss: '',
        damperPressureLoss: '',
        otherPressureLoss: '',
        total: ''
      }
    }
  }

  return {
    position: legacyFan.position || '',
    loadPercentage: legacyFan.loadPercentage || '',
    equipmentNumber: legacyFan.equipmentNumber || '',
    airflow: legacyFan.airflow || '',
    staticPressure: legacyFan.staticPressure || '',
    motorPower: legacyFan.motorPower || '',
    efficiency: legacyFan.efficiency || '',
    rpm: legacyFan.rpm || '',
    safetyFactor: legacyFan.safetyFactor || '',
    ductPressureLoss: legacyFan.ductPressureLoss || {
      supplyDuctFriction: '',
      supplyDuctLength: '',
      returnDuctFriction: '',
      returnDuctLength: '',
      supplyAirDevicePressureLoss: '',
      returnAirDevicePressureLoss: '',
      preFilterPressureLoss: '',
      mediumFilterPressureLoss: '',
      heatingCoilPressureLoss: '',
      coolingCoilPressureLoss: '',
      humidifierPressureLoss: '',
      soundAttenuatorPressureLoss: '',
      damperPressureLoss: '',
      otherPressureLoss: '',
      total: ''
    }
  }
}

/** 레거시 데이터 정리 */
const cleanupLegacyData = (): void => {
  try {
    // AHU Form 임시 데이터들 정리
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('KDCSCOKR_AHUSELECTION_FORM_') ||
          key.startsWith('fan-selection-form-data-')) {
        localStorage.removeItem(key)
      }
    })
    
    // 기타 레거시 키들 정리 (선택적)
    // localStorage.removeItem(STORAGE_KEYS.LEGACY_GLOBAL_STATE)
    // localStorage.removeItem(STORAGE_KEYS.LEGACY_UI_STATE)  
    // localStorage.removeItem(STORAGE_KEYS.LEGACY_FAN_SELECTION)
    
  } catch (error) {
    console.error('Failed to cleanup legacy data:', error)
  }
}

// =============================================
// 리듀서 함수
// =============================================

function unifiedStateReducer(state: UnifiedGlobalState, action: UnifiedAction): UnifiedGlobalState {
  let newState: UnifiedGlobalState

  switch (action.type) {
    case 'LOAD_FROM_STORAGE':
      return { ...action.payload, isLoaded: true }

    case 'SET_LOADED':
      return { ...state, isLoaded: action.payload }

    // 프로젝트 관리
    case 'ADD_PROJECT':
     
      newState = {
        ...state,
        projects: [...state.projects, action.payload],
        currentProjectId: action.payload.id
      }
      
      break

    case 'UPDATE_PROJECT':
      newState = {
        ...state,
        projects: state.projects.map(project =>
          project.id === action.payload.id ? action.payload : project
        )
      }
      break

    case 'DELETE_PROJECT': {
      const remainingProjects = state.projects.filter(p => p.id !== action.payload)
      const relatedAHUs = state.ahus.filter(ahu => ahu.projectId !== action.payload)
      newState = {
        ...state,
        projects: remainingProjects,
        ahus: relatedAHUs,
        currentProjectId: state.currentProjectId === action.payload
          ? (remainingProjects.length > 0 ? remainingProjects[0].id : null)
          : state.currentProjectId,
        currentAHUId: relatedAHUs.find(ahu => ahu.id === state.currentAHUId)
          ? state.currentAHUId
          : null
      }
      break
    }

    case 'SET_CURRENT_PROJECT':
      newState = {
        ...state,
        currentProjectId: action.payload,
        currentAHUId: action.payload
          ? (state.ahus.find(ahu => ahu.projectId === action.payload)?.id || null)
          : null
      }
      break

    // AHU 관리
    case 'ADD_AHU':
      newState = {
        ...state,
        ahus: [...state.ahus, action.payload],
        currentAHUId: action.payload.id
      }
      break

    case 'UPDATE_AHU':
      //console.log('🏪 [REDUCER UPDATE_AHU] Payload ID:', action.payload.id)
      //console.log('🏪 [REDUCER UPDATE_AHU] Payload calculationResults.sfLoadPercentage:', action.payload.calculationResults?.sfLoadPercentage)
      //console.log('🏪 [REDUCER UPDATE_AHU] Payload calculationResults.rfLoadPercentage:', action.payload.calculationResults?.rfLoadPercentage)
      //console.log('🏪 [REDUCER UPDATE_AHU] Full calculationResults:', action.payload.calculationResults)
      newState = {
        ...state,
        ahus: state.ahus.map(ahu =>
          ahu.id === action.payload.id ? action.payload : ahu
        )
      }
      //console.log('🏪 [REDUCER UPDATE_AHU] Updated AHU from state:', newState.ahus.find(a => a.id === action.payload.id)?.calculationResults)
      break

    case 'DELETE_AHU': {
      const remainingAHUs = state.ahus.filter(ahu => ahu.id !== action.payload)
      const currentProjectAHUs = remainingAHUs.filter(ahu => ahu.projectId === state.currentProjectId)
      newState = {
        ...state,
        ahus: remainingAHUs,
        currentAHUId: state.currentAHUId === action.payload
          ? (currentProjectAHUs.length > 0 ? currentProjectAHUs[0].id : null)
          : state.currentAHUId
      }
      break
    }

    case 'SET_CURRENT_AHU':
      newState = {
        ...state,
        currentAHUId: action.payload
      }
      break

    // AHU 폼 데이터 업데이트
    case 'UPDATE_AHU_FORM_DATA':
      newState = {
        ...state,
        ahus: state.ahus.map(ahu =>
          ahu.id === action.payload.ahuId
            ? { ...ahu, formData: { ...ahu.formData, ...action.payload.formData }, updatedAt: new Date().toISOString() }
            : ahu
        )
      }
      break

    case 'UPDATE_FAN_SELECTION_DATA':
      newState = {
        ...state,
        ahus: state.ahus.map(ahu =>
          ahu.id === action.payload.ahuId
            ? { ...ahu, fanSelectionData: action.payload.fanData, updatedAt: new Date().toISOString() }
            : ahu
        )
      }
      break

    // UI 상태
    case 'SET_ACTIVE_FORM':
      newState = {
        ...state,
        ui: { ...state.ui, activeForm: action.payload }
      }
      break

    case 'SET_ACTIVE_TAB':
      newState = {
        ...state,
        ui: { ...state.ui, activeTab: action.payload }
      }
      break

    case 'SET_SIDEBAR_COLLAPSED':
      newState = {
        ...state,
        ui: { ...state.ui, sidebarCollapsed: action.payload }
      }
      break

    // 임시 데이터
    case 'SET_TEMP_CALCULATION_RESULTS':
      newState = {
        ...state,
        tempData: {
          ...state.tempData,
          [action.payload.type === 'ahu' ? 'ahuCalculationResults' : 'fanCalculationResults']: action.payload.data
        }
      }
      break

    case 'CLEAR_TEMP_DATA':
      newState = {
        ...state,
        tempData: {
          ahuCalculationResults: null,
          fanCalculationResults: null
        }
      }
      break

    // 시스템
    case 'UPDATE_SETTINGS':
      newState = {
        ...state,
        settings: { ...state.settings, ...action.payload }
      }
      break

    case 'RESET_ALL_DATA':
      cleanupLegacyData()
      return initialState

    case 'IMPORT_DATA':
      newState = {
        ...state,
        projects: action.payload.projects,
        ahus: action.payload.ahus,
        currentProjectId: action.payload.projects.length > 0 ? action.payload.projects[0].id : null,
        currentAHUId: action.payload.ahus.length > 0 ? action.payload.ahus[0].id : null
      }
      break

    default:
      return state
  }

  // 자동으로 로컬스토리지에 저장 (LOAD_FROM_STORAGE 제외)
  if (action.type !== 'LOAD_FROM_STORAGE') {
    saveToStorage(newState)
  }

  return newState
}

// =============================================
// Context 생성 및 Provider
// =============================================

const UnifiedStateContext = createContext<{
  state: UnifiedGlobalState
  dispatch: React.Dispatch<UnifiedAction>
} | null>(null)

export function UnifiedStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(unifiedStateReducer, initialState)

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    const storedState = loadFromStorage()
    if (storedState) {
      dispatch({ type: 'LOAD_FROM_STORAGE', payload: { ...initialState, ...storedState } })
      
      // 마이그레이션 후 레거시 데이터 정리 (옵션)
      // cleanupLegacyData()
    } else {
      dispatch({ type: 'SET_LOADED', payload: true })
    }
  }, [])

  return (
    <UnifiedStateContext.Provider value={{ state, dispatch }}>
      {children}
    </UnifiedStateContext.Provider>
  )
}

export function useUnifiedState() {
  const context = useContext(UnifiedStateContext)
  if (!context) {
    throw new Error('useUnifiedState must be used within a UnifiedStateProvider')
  }
  return context
}

// =============================================
// 헬퍼 함수들
// =============================================

/** 새 프로젝트 생성 */
export const createProject = (projectData: Omit<ProjectInfo, 'id' | 'createdAt' | 'updatedAt'>): ProjectInfo => ({
  ...projectData,
  id: `project-${Date.now()}`,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
})

/** 새 AHU 생성 */
export const createAHU = (ahuData: Omit<AHUData, 'id' | 'createdAt' | 'updatedAt'>): AHUData => ({
  ...ahuData,
  id: `ahu-${Date.now()}`,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
})

/** 데이터 내보내기 */
export const exportUnifiedData = (state: UnifiedGlobalState): string => {
  const exportData = {
    projects: state.projects,
    ahus: state.ahus,
    settings: state.settings,
    exportedAt: new Date().toISOString(),
    version: state.version
  }
  
  return JSON.stringify(exportData, null, 2)
}

/** 데이터 가져오기 */
export const importUnifiedData = (jsonData: string): { projects: ProjectInfo[]; ahus: AHUData[] } => {
  try {
    const data = JSON.parse(jsonData)
    
    if (!data.projects || !data.ahus) {
      throw new Error('Invalid data format')
    }
    
    // ID 충돌 방지를 위해 새로운 ID 생성
    const now = new Date().toISOString()
    const projects: ProjectInfo[] = data.projects.map((project: ProjectInfo, index: number) => ({
      ...project,
      id: `imported-project-${Date.now()}-${index}`,
      updatedAt: now
    }))
    
    const ahus: AHUData[] = data.ahus.map((ahu: AHUData, index: number) => {
      const originalProjectIndex = data.projects.findIndex((p: ProjectInfo) => p.id === ahu.projectId)
      const newProjectId = originalProjectIndex >= 0 ? projects[originalProjectIndex].id : projects[0]?.id
      
      return {
        ...ahu,
        id: `imported-ahu-${Date.now()}-${index}`,
        projectId: newProjectId,
        updatedAt: now
      }
    })
    
    return { projects, ahus }
  } catch (error) {
    throw new Error('Failed to import data: Invalid JSON format')
  }
}

// 타입 export
export type { UnifiedGlobalState, UnifiedAction, ProjectInfo, AHUData, AHUFormData, FanSelectionData }